/**
 * Utility functions for handling circular references in objects
 */

/**
 * Format special types for better readability
 * @param obj - Object to format
 * @returns Formatted object or null if not applicable
 */
const formatSpecialTypes = ( obj: unknown ): unknown => {
    if ( obj instanceof Date ) {
        return obj.toISOString();
    }

    if ( obj instanceof Error ) {
        return {
            "name":    obj.name,
            "message": obj.message,
            "stack":   obj.stack?.split( "\n" ).slice( 0, 3 ),
        };
    }

    if ( obj instanceof RegExp ) {
        return obj.toString();
    }

    // Détecter les coordonnées [lng, lat]
    if ( Array.isArray( obj ) && obj.length === 2 && typeof obj[0] === "number" && typeof obj[1] === "number" ) {
        return `[${ obj[0].toFixed( 6 ) }, ${ obj[1].toFixed( 6 ) }]`;
    }

    return null; // Non applicable
};

/**
 * Extract useful properties from Error objects and MapLibre events
 * @param obj - Object to extract from
 * @returns Simplified object with useful properties
 */
const extractUsefulProperties = ( obj: unknown ): unknown => {
    if ( obj === null || typeof obj !== "object" ) {
        return obj;
    }

    // Handle Error objects
    if ( obj instanceof Error ) {
        return {
            "name":    obj.name,
            "message": obj.message,
            "stack":   obj.stack,
        };
    }

    // Handle MapLibre/DOM events and other objects with known circular properties
    const objAsRecord = obj as Record<string, unknown>;

    // Extract useful primitive properties only
    const useful: Record<string, unknown> = {};

    for ( const [ key, value ] of Object.entries( objAsRecord ) ) {
        const typeOfValue = typeof value;

        // Skip functions and symbols
        if ( typeOfValue === "function" || typeOfValue === "symbol" ) {
            continue;
        }

        // Skip DOM elements and MapLibre map instances
        if ( value && typeof value === "object" ) {
            // Skip if it looks like a DOM element
            if ( "nodeType" in value || "tagName" in value ) {
                continue;
            }

            // Skip if it looks like a Map instance
            if ( "_container" in value && "_style" in value ) {
                continue;
            }

            // Skip if it looks like a React object (has React-specific properties)
            if ( "_reactInternalFiber" in value || "_reactInternalInstance" in value || "$$typeof" in value ) {
                continue;
            }

            // Skip if it looks like a MapLibre camera config (has deep nested properties)
            if ( "center" in value && "zoom" in value && "bearing" in value ) {
                // Extract only the essential camera properties
                const cameraProps = {
                    "center":  ( value as Record<string, unknown> ).center,
                    "zoom":    ( value as Record<string, unknown> ).zoom,
                    "bearing": ( value as Record<string, unknown> ).bearing,
                    "pitch":   ( value as Record<string, unknown> ).pitch,
                };
                useful[key] = cameraProps;

                continue;
            }
        }

        // Keep primitives and simple properties
        if ( typeOfValue === "string" || typeOfValue === "number" || typeOfValue === "boolean" ) {
            useful[key] = value;
        } else if ( value === null || value === undefined ) {
            useful[key] = value;
        } else if ( key === "type" || key === "message" || key === "error" || key === "sourceId" || key === "code" ) {
            // Keep specific important properties
            useful[key] = value;
        }
    }

    return Object.keys( useful ).length > 0 ? useful : "[Complex Object]";
};

/**
 * Clean circular references from an object before serialization
 * @param obj - Object to clean
 * @param seen - WeakSet to track visited objects (for recursion)
 * @param depth - Current recursion depth (to prevent infinite loops)
 * @param maxDepth - Maximum recursion depth allowed
 * @returns Cleaned object without circular references
 */
export const cleanCircularReferences = (
    obj: unknown,
    seen = new WeakSet(),
    depth = 0,
    maxDepth = 10,
): unknown => {
    if ( obj === null || typeof obj !== "object" ) {
        return obj;
    }

    // Ajouter formatage spécial en premier
    const formatted = formatSpecialTypes( obj );
    if ( formatted !== null ) {
        return formatted;
    }

    /**
     * If we've seen this object before, it's a circular reference.
     * Check this BEFORE checking maxDepth to properly handle circular refs
     */
    if ( seen.has( obj ) ) {
        return extractUsefulProperties( obj );
    }

    // Prevent infinite recursion
    if ( depth > maxDepth ) {
        // Try to extract useful properties even at max depth
        return extractUsefulProperties( obj );
    }

    seen.add( obj );

    // Handle arrays
    if ( Array.isArray( obj ) ) {
        return obj.map( ( item ) => cleanCircularReferences( item, seen, depth + 1, maxDepth ) );
    }

    // Handle objects
    const cleaned: Record<string, unknown> = {};
    for ( const [ key, value ] of Object.entries( obj as Record<string, unknown> ) ) {
        // Skip functions
        if ( typeof value === "function" ) {
            continue;
        }

        cleaned[key] = cleanCircularReferences( value, seen, depth + 1, maxDepth );
    }

    return cleaned;
};

/**
 * Safely stringify an object, handling circular references
 * @param obj - Object to stringify
 * @returns JSON string or error message
 */
export const safeJsonStringify = ( obj: unknown ): string => {
    try {
        return JSON.stringify( cleanCircularReferences( obj ) );
    } catch( error ) {
        return `[Stringify Error: ${ error }]`;
    }
};
